import { Box } from '@chakra-ui/react'
import { ReactNode, useEffect, useRef } from 'react'

interface TabSwipePagerProps {
  index: number
  count: number
  onChangeIndex: (i: number) => void
  onSwipeRightOnFirst?: () => void
  enabled?: boolean
  // Opt in to passing the parent's remaining desktop height down to the page.
  // Only for pages that scroll internally; others need to grow freely.
  fullHeight?: boolean
  children: ReactNode
}

// Drag-to-switch pager for tab content.
//
// During a drag we visually translate the track to follow the finger; on
// release, if the gesture passes a distance/velocity threshold we commit:
// animate the current page out, then call `onChangeIndex` so the parent
// router swaps the children. Because the parent re-renders with a new `index`
// and new children, we remember the commit direction and on the next render
// instantly position the track at the opposite side, then animate it back to
// 0. The result feels continuous instead of cutting to the new page.
//
// On the first tab, a rightward drag does not navigate to a previous tab.
// If `onSwipeRightOnFirst` is provided, releasing past threshold fires it
// (used to open the side menu); the visual snaps back.
const TabSwipePager = ({
  index,
  count,
  onChangeIndex,
  onSwipeRightOnFirst,
  enabled = true,
  fullHeight = false,
  children,
}: TabSwipePagerProps) => {
  const trackRef = useRef<HTMLDivElement | null>(null)
  const state = useRef<{
    startX: number
    startY: number
    startT: number
    dragging: boolean
    locked: 'h' | 'v' | null
    width: number
    rawDx: number
  }>({ startX: 0, startY: 0, startT: 0, dragging: false, locked: null, width: 0, rawDx: 0 })

  // Direction of the last committed swipe. After index changes we use this to
  // start the new page off-screen and animate it in.
  const pendingEntry = useRef<'left' | 'right' | null>(null)

  const setTransform = (px: number, animated: boolean) => {
    const el = trackRef.current
    if (!el) return
    el.style.transition = animated ? 'transform 260ms cubic-bezier(0.22, 1, 0.36, 1)' : 'none'
    // Only set transform when there's an actual offset. Any non-`none`
    // transform creates a containing block for `position: fixed` descendants
    // (bulk action portal, modals), so we must clear it when at rest.
    if (px === 0) {
      el.style.transform = ''
      el.style.willChange = ''
    } else {
      el.style.willChange = 'transform'
      el.style.transform = `translateX(${px}px)`
    }
  }

  const clearAfter = (delay: number) => {
    const el = trackRef.current
    if (!el) return
    window.setTimeout(() => {
      if (!el) return
      el.style.transform = ''
      el.style.willChange = ''
      el.style.transition = ''
    }, delay)
  }

  // Animate the new page in after a committed swipe.
  useEffect(() => {
    const el = trackRef.current
    if (!el) return
    const entry = pendingEntry.current
    if (!entry) {
      setTransform(0, false)
      return
    }
    const width = el.clientWidth || window.innerWidth
    const startX = entry === 'left' ? width : -width
    setTransform(startX, false)
    // Force reflow so the browser registers the starting position before
    // we transition to 0.
    void el.offsetWidth
    requestAnimationFrame(() => {
      setTransform(0, true)
      clearAfter(280)
    })
    pendingEntry.current = null
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index])

  // Walk from the touch target up to the pager track and check whether any
  // ancestor is a horizontally-scrollable element with scrollable content (an
  // inner carousel/row). If so, the inner scroller should own the horizontal
  // gesture — the pager bails out so it doesn't swipe to the next tab when the
  // user actually wants to scroll the row sideways.
  const isInsideHorizontalScroller = (target: EventTarget | null): boolean => {
    let node = target as HTMLElement | null
    const stop = trackRef.current
    while (node && node !== stop) {
      // Only consider elements that actually have horizontally-scrollable content.
      if (node.scrollWidth > node.clientWidth + 1) {
        const style = window.getComputedStyle(node)
        const ox = style.overflowX
        if (ox === 'auto' || ox === 'scroll') return true
      }
      node = node.parentElement
    }
    return false
  }

  const onTouchStart = (e: React.TouchEvent) => {
    if (!enabled) return
    const t = e.touches[0]
    if (!t) return
    if (isInsideHorizontalScroller(e.target)) {
      // Disengage entirely — let the inner scroller handle this gesture.
      state.current.dragging = false
      state.current.locked = null
      return
    }
    state.current = {
      startX: t.clientX,
      startY: t.clientY,
      startT: performance.now(),
      dragging: true,
      locked: null,
      width: trackRef.current?.clientWidth ?? window.innerWidth,
      rawDx: 0,
    }
  }

  const onTouchMove = (e: React.TouchEvent) => {
    if (!enabled) return
    const s = state.current
    if (!s.dragging) return
    const t = e.touches[0]
    if (!t) return
    const dx = t.clientX - s.startX
    const dy = t.clientY - s.startY
    s.rawDx = dx

    if (s.locked === null) {
      const absX = Math.abs(dx)
      const absY = Math.abs(dy)
      if (absX < 10 && absY < 10) return
      s.locked = absX > absY ? 'h' : 'v'
      if (s.locked === 'v') {
        s.dragging = false
        return
      }
    }

    if (s.locked !== 'h') return

    // Resistance at edges (first tab swipe-right, last tab swipe-left)
    let translate = dx
    const atLeftEdge = index === 0 && dx > 0
    const atRightEdge = index === count - 1 && dx < 0
    if (atLeftEdge || atRightEdge) {
      translate = dx * 0.4
    }
    setTransform(translate, false)
  }

  const onTouchEnd = () => {
    if (!enabled) return
    const s = state.current
    if (!s.dragging || s.locked !== 'h') {
      s.dragging = false
      s.locked = null
      return
    }
    s.dragging = false
    s.locked = null

    const el = trackRef.current
    if (!el) return
    const matrix = new DOMMatrix(getComputedStyle(el).transform)
    const currentX = matrix.m41
    const elapsed = performance.now() - s.startT
    const rawDx = s.rawDx
    // Velocity based on the raw gesture, not the translated value (which is
    // dampened at the edges).
    const velocity = elapsed > 0 ? Math.abs(rawDx) / elapsed : 0
    const threshold = Math.min(80, s.width * 0.18)
    const edgeThreshold = Math.min(70, s.width * 0.16)
    const fastFlick = velocity > 0.5 && Math.abs(rawDx) > 24

    const goNext = currentX < 0 && (Math.abs(currentX) > threshold || fastFlick) && index < count - 1
    const goPrev = currentX > 0 && (Math.abs(currentX) > threshold || fastFlick) && index > 0
    const triggerEdge =
      rawDx > 0 && (rawDx > edgeThreshold || fastFlick) && index === 0 && !!onSwipeRightOnFirst

    if (goNext) {
      pendingEntry.current = 'left'
      setTransform(-s.width, true)
      window.setTimeout(() => onChangeIndex(index + 1), 200)
    } else if (goPrev) {
      pendingEntry.current = 'right'
      setTransform(s.width, true)
      window.setTimeout(() => onChangeIndex(index - 1), 200)
    } else if (triggerEdge) {
      setTransform(0, true)
      clearAfter(280)
      onSwipeRightOnFirst?.()
    } else {
      setTransform(0, true)
      clearAfter(280)
    }
  }

  return (
    // h/flex on desktop pass the parent's remaining height down to the page;
    // the swipe track itself is mobile-only, so this is inert there.
    <Box
      w="100%"
      overflowX="clip"
      position="relative"
      sx={{ touchAction: 'pan-y' }}
      flex={fullHeight ? { md: 1 } : undefined}
      minH={fullHeight ? { md: 0 } : undefined}
      display={fullHeight ? { md: 'flex' } : undefined}
      flexDirection={fullHeight ? { md: 'column' } : undefined}
    >
      <Box
        ref={trackRef}
        w="100%"
        flex={fullHeight ? { md: 1 } : undefined}
        minH={fullHeight ? { md: 0 } : undefined}
        display={fullHeight ? { md: 'flex' } : undefined}
        flexDirection={fullHeight ? { md: 'column' } : undefined}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
      >
        {children}
      </Box>
    </Box>
  )
}

export default TabSwipePager
